import React, { useEffect, useMemo, useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { Modal } from '../core';
import { Button } from '../core';
import { useUpdateTimetracking } from '../../graphql/hooks/useAdminDashboard';
import toast from "react-hot-toast";

type Place = {
  id: number | string
  client?: string
  duration: number
  comment?: string | null
  is_night_work: boolean
  is_emergency_service: boolean
};

export type HoursDetailsData = {
  date: string
  total_hours: string 
  emergency: boolean
  nightWork: boolean
  places: Place[]
};

type Props = {
  isOpen: boolean
  onClose: () => void
  data: HoursDetailsData | null
};

export const HoursDetailsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  data,
}) => {
  const anyEmergencyInPlaces = useMemo(() => {
    if (!data?.places?.length) return false
    return data.places.some((p) => !!p.is_emergency_service)
  }, [data])

  const [openDropdownId, setOpenDropdownId] = useState<number | string | null>(null);
  const [editingPlaceId, setEditingPlaceId] = useState<number | string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [emergencyChecked, setEmergencyChecked] = useState(false);
  const [client, setClient] = useState('');
  const [duration, setDuration] = useState<string>('');
  const [comment, setComment] = useState('');
  const [durationError, setDurationError] = useState<string | null>(null);
  const [places, setPlaces] = useState<Place[]>([]);

  const { mutate: updateTimetrackingMutate } = useUpdateTimetracking();

  const prefillFromData = () => {
    if (!data) return
    setEmergencyChecked(anyEmergencyInPlaces)

    const first = data.places?.[0]
    setClient(first?.client ?? '')
    setDuration(first?.duration != null ? String(first.duration) : '')
    setComment(first?.comment ?? '')
  };

  useEffect(() => {
    if (!isOpen || !data) return;
    setOpenDropdownId(null)
    setIsEditMode(false);
    prefillFromData();
    setPlaces(data.places ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, data, anyEmergencyInPlaces]);

  if (!isOpen || !data) return null;

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditingPlaceId(null);
    setOpenDropdownId(null);
    prefillFromData();
  };

  const isValidDuration = (value: string) => {
    return /^(\d+):([0-5][0-9])$/.test(value);
  };

  const formatTime = (input: string) => {
    if (!input) return "";

    const value = input.replace(",", ".").trim();
    let hours;
    let minutes;

    if (value.includes(":")) {
      const [h, m = "0"] = value.split(":");
      hours = parseInt(h, 10);
      minutes = parseInt(m, 10);
    } else {
      const number = parseFloat(value);
      if (isNaN(number)) return "";

      hours = Math.floor(number);
      minutes = Math.round((number - hours) * 60);
    }

    if (isNaN(hours) || isNaN(minutes)) return "";

    if (minutes >= 60) {
      hours += Math.floor(minutes / 60);
      minutes = minutes % 60;
    }

    return `${hours}:${minutes.toString().padStart(2, "0")}`;
  };

  const handleSave = () => {
    const durationToFloat = (value: string) => {
      const [hours, minutes] = value.split(":").map(Number);
      return hours + minutes / 60;
    };

    const d = durationToFloat(duration);
    if (!client.trim()) return;

    const place = places.find((p) => Number(p.id) === Number(editingPlaceId));
    if (!place) return;

    updateTimetrackingMutate(
      {
        id: Number(place.id),
        client: client.trim(),
        duration: d,
        comment: comment ?? "",
        is_emergency_service: emergencyChecked,
        is_night_work: place.is_night_work,
      },
      {
        onSuccess: () => {
          const formattedDate = new Date(data.date).toLocaleDateString("de-DE");

            setPlaces((prev) =>
              prev.map((p) =>
                Number(p.id) === Number(place.id)
                  ? {
                      ...p,
                      client,
                      duration: d,
                      comment,
                      is_emergency_service: emergencyChecked,
                    }
                  : p
              )
            );

          toast.success(
            `Eintrag gespeichert • ${formattedDate}${client ? ` • ${client}` : ""}`,
            {
              duration: 3000,
              style: {
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                color: "#15803d",
                padding: "12px 14px",
                borderRadius: "8px",
                fontSize: "14px",
              },
              iconTheme: {
                primary: "#16a34a",
                secondary: "#f0fdf4",
              },
            }
          );

          setIsEditMode(false);
          setEditingPlaceId(null);
        },
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" title={data.date}>
      <div className="space-y-6">
        <div className="flex justify-end">
          <label className="flex items-center gap-2 text-[14px] text-[#4f46e5] select-none">
            <input
              type="checkbox"
              checked={emergencyChecked}
              onChange={(e) => setEmergencyChecked(e.target.checked)}
              className="size-4 rounded border-[#e0e0e0]"
            />
            Notdienst
          </label>
        </div>

        <div className="border border-[#e9e9e9] rounded-[10px] overflow-visible">
          <div className="grid grid-cols-[1fr_0.8fr_2fr_44px] gap-4 px-4 py-3 bg-[#f5f5f5] text-[14px] font-medium text-[#6b6b6b]">
            <div>Kunde</div>
            <div>Dauer</div>
            <div>Kommentar</div>
            <div />
          </div>

          {places?.length ? (
            places.map((p) => (
              <div
                key={String(p.id)}
                className="grid grid-cols-[1fr_0.8fr_2fr_44px] gap-4 px-4 py-3 text-[14px] items-center border-t border-[#e9e9e9]"
              >
                <div className="text-[#1c1c1c]">{p.client || '-'}</div>
                <div className="text-[#1c1c1c]">
                  {typeof p.duration === 'number' ? `${p.duration} Std` : '-'}
                </div>
                <div className="text-[#1c1c1c]">
                  {p.comment ?? ''}
                </div>

                <div className="relative flex justify-end">
                  <button
                    onClick={() =>
                      setOpenDropdownId((prev) => (prev === p.id ? null : p.id))
                    }
                    className="p-2 rounded-[6px] hover:bg-[#f5f5f5] transition-colors"
                    aria-label="Mehr"
                  >
                    <MoreVertical className="size-5 text-[#6b6b6b]" />
                  </button>

                  {openDropdownId === p.id && !isEditMode && (
                    <div className="absolute right-0 top-10 bg-white border border-[#e9e9e9] rounded-[6px] shadow-lg z-10 min-w-[160px] overflow-hidden">
                      <button
                        onClick={() => {
                          setEditingPlaceId(p.id)
                          setIsEditMode(true)
                          setOpenDropdownId(null)

                          setClient(p.client ?? "")
                          setDuration(String(p.duration))
                          setComment(p.comment ?? "")
                          setEmergencyChecked(p.is_emergency_service)
                        }}
                        className="w-full px-4 py-2.5 text-left text-[14px] text-[#1c1c1c] hover:bg-[#f5f5f5] transition-colors"
                      >
                        Bearbeiten
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="px-4 py-6 text-[#6b6b6b] text-[14px] text-center">
              Keine Daten vorhanden
            </div>
          )}
        </div>

        {isEditMode && (
          <div className="space-y-5">
            <div>
              <p className="text-[#6b6b6b] text-[14px] mb-2">Kunde:</p>
              <input
                value={client}
                onChange={(e) => setClient(e.target.value)}
                placeholder="Hier Kunde eintragen..."
                className="w-full box-border h-[38px] px-3 border border-[#e0e0e0] rounded-[6px]"
              />
            </div>

            <div>
              <p className="text-[#6b6b6b] text-[14px] mb-2">Dauer:</p>

              <input
                value={duration}
                onChange={(e) => {
                  const value = e.target.value;

                  setDuration(value);

                  if (!value) {
                    setDurationError(null);
                  } else if (!isValidDuration(value)) {
                    setDurationError("Bitte Stunden im Format 5 oder 5:30 eingeben");
                  } else {
                    setDurationError(null);
                  }
                }}
                onBlur={() => {
                  if (!duration) return;

                  const formatted = formatTime(duration);

                  if (!formatted || !isValidDuration(formatted)) {
                    setDurationError("Bitte Stunden im Format 5 oder 5:30 eingeben");
                    return;
                  }

                  setDuration(formatted);
                  setDurationError(null);
                }}
                className={`w-full box-border h-[38px] px-3 border rounded-[6px]
                  ${
                    durationError
                      ? "border-red-400 focus:ring-red-300"
                      : "border-[#e0e0e0] focus:ring-[#0084c7]"
                  }`}
              />

              {durationError && (
                <p className="text-red-500 text-[12px] mt-1">{durationError}</p>
              )}
            </div>

            <div>
              <p className="text-[#6b6b6b] text-[14px] mb-2">Bemerkung:</p>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                className="w-full box-border px-4 py-2 border border-[#e0e0e0] rounded-[8px]"
              />
            </div>

            <div className="flex items-center gap-3">
              <Button variant="primary" onClick={handleSave}>
                Aktualisieren
              </Button>
              <Button variant="secondary" onClick={handleCancelEdit}>
                Abbrechen
              </Button>
            </div>
          </div>
        )}

        {/* Footer like your other modals */}
        <div className="flex justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        </div>
      </div>
    </Modal>
  )
}